/**
 * Context de Autenticación con Azure AD usando MSAL
 * 
 * Proporciona:
 * - AuthProvider: Wrapper para la aplicación
 * - useAuth: Hook para acceder al estado de autenticación
 * - Funciones de login/logout
 * - Información del usuario autenticado
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { 
  PublicClientApplication, 
  AccountInfo, 
  EventType, 
  AuthenticationResult,
  SilentRequest,
  EndSessionRequest,
} from '@azure/msal-browser';
import { msalConfig, loginRequest, graphConfig, apiConfig, isAuthDisabled, mockDevUser, SUPERADMIN_EMAILS } from '../config/authConfig';

// Tipos para el usuario autenticado
export interface AuthUser {
  id: string;
  name: string;
  email: string;
  username: string;
  roles: string[];
  photoUrl?: string;
  accessToken?: string;
}

// Tipos para el contexto de autenticación
interface AuthContextType {
  // Estado
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isSuperAdmin: boolean;
  
  // Funciones
  login: () => Promise<void>;
  loginRedirect: () => Promise<void>;
  logout: () => void;
  getAccessToken: (scopes?: string[]) => Promise<string | null>;
  hasRole: (role: string) => boolean;
  
  // MSAL instance (para uso avanzado)
  msalInstance: PublicClientApplication;
}

// Crear el contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Instancia de MSAL (singleton)
const msalInstance = new PublicClientApplication(msalConfig);

// Inicializar MSAL
const initializeMsal = async () => {
  await msalInstance.initialize();
  
  // Manejar respuestas de redirect
  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      console.log('[Auth] Redirect response received:', response.account?.username);
    }
  } catch (error) {
    console.error('[Auth] Error handling redirect:', error);
  }
};

// Props del provider
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider - Componente que envuelve la aplicación y proporciona autenticación
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Si la autenticación está deshabilitada, usar usuario mock
  const authDisabled = isAuthDisabled();

  // When auth is disabled, start with the mock user already set so child
  // components receive a valid email from the very first render.
  const [user, setUser] = useState<AuthUser | null>(authDisabled ? (mockDevUser as AuthUser) : null);
  const [isLoading, setIsLoading] = useState(!authDisabled);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(authDisabled);

  useEffect(() => {
    if (authDisabled) {
      console.warn('🚧 [Auth] MODO DESARROLLO: Autenticación deshabilitada');
      console.warn('🚧 [Auth] Usuario mock:', mockDevUser.email);
      console.warn('⚠️  [Auth] NUNCA usar REACT_APP_DISABLE_AUTH=true en producción');
      // State is already initialized synchronously above; nothing to do here.
      return;
    }
  }, [authDisabled]);

  // Convertir AccountInfo de MSAL a AuthUser
  const accountToUser = useCallback(async (account: AccountInfo): Promise<AuthUser> => {
    // Extraer roles del token (si están disponibles)
    const roles: string[] = (account.idTokenClaims as any)?.roles || [];
    
    // Intentar obtener la foto del usuario desde Microsoft Graph
    let photoUrl: string | undefined;
    try {
      const tokenResult = await msalInstance.acquireTokenSilent({
        scopes: ['User.Read'],
        account: account,
      });
      
      const response = await fetch(graphConfig.graphPhotoEndpoint, {
        headers: {
          'Authorization': `Bearer ${tokenResult.accessToken}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        photoUrl = URL.createObjectURL(blob);
      }
    } catch (e) {
      // La foto no es obligatoria, ignorar errores
      console.debug('[Auth] Could not fetch user photo:', e);
    }

    return {
      id: account.localAccountId,
      name: account.name || 'Usuario',
      email: account.username,
      username: account.username,
      roles,
      photoUrl,
    };
  }, []);

  // Inicializar MSAL y verificar si hay sesión activa
  useEffect(() => {
    // Si la autenticación está deshabilitada, no inicializar MSAL
    if (authDisabled) {
      return;
    }

    const initialize = async () => {
      try {
        await initializeMsal();
        
        // Verificar si hay una cuenta activa
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const account = accounts[0];
          msalInstance.setActiveAccount(account);
          const authUser = await accountToUser(account);
          setUser(authUser);
        }
        
        setIsInitialized(true);
      } catch (err) {
        console.error('[Auth] Initialization error:', err);
        setError('Error al inicializar la autenticación');
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, [accountToUser, authDisabled]);

  // Escuchar eventos de MSAL
  useEffect(() => {
    if (authDisabled || !isInitialized) return;

    const callbackId = msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        const result = event.payload as AuthenticationResult;
        if (result.account) {
          msalInstance.setActiveAccount(result.account);
          accountToUser(result.account).then(setUser);
        }
      }
      
      if (event.eventType === EventType.LOGOUT_SUCCESS) {
        setUser(null);
      }
      
      if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        console.error('[Auth] Token acquisition failed:', event.error);
      }
    });

    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, [isInitialized, accountToUser, authDisabled]);

  // Login con popup
  const login = useCallback(async () => {
    if (authDisabled) {
      console.warn('[Auth] Login llamado en modo desarrollo - autenticación deshabilitada');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await msalInstance.loginPopup(loginRequest);
      
      if (response.account) {
        msalInstance.setActiveAccount(response.account);
        const authUser = await accountToUser(response.account);
        setUser(authUser);
      }
    } catch (err: any) {
      console.error('[Auth] Login error:', err);
      
      // Manejar cancelación del usuario
      if (err.errorCode === 'user_cancelled') {
        setError('El usuario canceló el inicio de sesión');
      } else {
        setError(err.message || 'Error al iniciar sesión');
      }
    } finally {
      setIsLoading(false);
    }
  }, [accountToUser, authDisabled]);

  // Login con redirect (alternativa al popup)
  const loginRedirect = useCallback(async () => {
    if (authDisabled) {
      console.warn('[Auth] Login redirect llamado en modo desarrollo - autenticación deshabilitada');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      await msalInstance.loginRedirect(loginRequest);
      // El usuario será redirigido, no hay nada más que hacer aquí
    } catch (err: any) {
      console.error('[Auth] Login redirect error:', err);
      setError(err.message || 'Error al iniciar sesión');
      setIsLoading(false);
    }
  }, [authDisabled]);

  // Logout
  const logout = useCallback(() => {
    if (authDisabled) {
      console.warn('[Auth] Logout llamado en modo desarrollo - no se puede cerrar sesión en modo mock');
      return;
    }
    
    const logoutRequest: EndSessionRequest = {
      account: msalInstance.getActiveAccount(),
      postLogoutRedirectUri: window.location.origin,
    };
    
    msalInstance.logoutPopup(logoutRequest).catch((err) => {
      console.error('[Auth] Logout error:', err);
      // Forzar limpieza local si falla el logout remoto
      setUser(null);
    });
  }, [authDisabled]);

  // Obtener access token (para llamadas a APIs protegidas)
  const getAccessToken = useCallback(async (scopes?: string[]): Promise<string | null> => {
    // En modo desarrollo sin autenticación, retornar token mock o null
    if (authDisabled) {
      console.debug('[Auth] getAccessToken en modo desarrollo - retornando null');
      return null;
    }
    
    const account = msalInstance.getActiveAccount();
    if (!account) {
      console.warn('[Auth] No active account found');
      return null;
    }

    const tokenRequest: SilentRequest = {
      scopes: scopes || apiConfig.scopes.length > 0 ? apiConfig.scopes : ['User.Read'],
      account: account,
    };

    try {
      const response = await msalInstance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (err) {
      console.warn('[Auth] Silent token acquisition failed, trying popup:', err);
      
      try {
        const response = await msalInstance.acquireTokenPopup(tokenRequest);
        return response.accessToken;
      } catch (popupErr) {
        console.error('[Auth] Token acquisition failed:', popupErr);
        return null;
      }
    }
  }, [authDisabled]);

  // Verificar si el usuario tiene un rol específico
  const hasRole = useCallback((role: string): boolean => {
    return user?.roles.includes(role) || false;
  }, [user]);

  const isSuperAdmin = SUPERADMIN_EMAILS.includes((user?.email || '').toLowerCase());

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    isSuperAdmin,
    login,
    loginRedirect,
    logout,
    getAccessToken,
    hasRole,
    msalInstance,
  };

  // Mostrar loading mientras se inicializa (solo si auth está habilitado)
  if (!authDisabled && !isInitialized && isLoading) {
    return (
      <AuthContext.Provider value={value}>
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Inicializando autenticación...</p>
          </div>
        </div>
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook para acceder al contexto de autenticación
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Hook para verificar si el usuario está autenticado
 */
export const useIsAuthenticated = (): boolean => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
};

/**
 * Hook para obtener el usuario actual
 */
export const useUser = (): AuthUser | null => {
  const { user } = useAuth();
  return user;
};

export { msalInstance };
